import { useState, useCallback, useEffect, useRef } from 'react';
import type { AuthUser } from '@roll1/shared';
import { setAccessToken, getAccessToken, apiFetch, doRefresh } from '../lib/api.js';
import { useI18n } from '../i18n';

/** Proactively refresh access token before expiry (every 50 min for 1h TTL) */
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

export interface UseAuthReturn extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, inviteCode: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (displayName: string) => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const { t } = useI18n();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  // Try to restore session on mount via refresh token cookie
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (res.ok) {
          const data = await res.json();
          setAccessToken(data.accessToken);

          // Fetch user profile
          const meRes = await apiFetch('/api/auth/me');
          if (meRes.ok) {
            const userData = await meRes.json();
            setUser(userData);
          }
        }
      } catch {
        // No session — stay logged out
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Proactively refresh token before expiry
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => { doRefresh(); }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('authError.loginError'));
        return;
      }

      setAccessToken(data.accessToken);
      setUser(data.user);
    } catch {
      setError(t('authError.networkError'));
    }
  }, [t]);

  const register = useCallback(async (email: string, password: string, displayName: string, inviteCode: string) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, displayName, inviteCode }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('authError.registerError'));
        return;
      }

      setAccessToken(data.accessToken);
      setUser(data.user);
    } catch {
      setError(t('authError.networkError'));
    }
  }, [t]);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Proceed with local cleanup even if request fails
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (displayName: string) => {
    setError(null);
    try {
      const res = await apiFetch('/api/auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('authError.updateError'));
        return;
      }

      setUser(data);
    } catch {
      setError(t('authError.networkError'));
    }
  }, [t]);

  return { user, loading, error, login, register, logout, updateProfile };
}
